/**
 * OAuth 2.0 Authorization Code + PKCE for a PUBLIC client (no secret).
 *
 * The SPA never holds a secret: login (email/pass + social) happens on the
 * auth-website's hosted page; we only initiate the redirect and exchange the
 * returned `code` for tokens using a PKCE verifier. All auth traffic goes
 * straight to the auth server — no website backend in between.
 */
import { env } from '@/config/env';
import { mapTokens } from '@/features/auth/api/auth.mappers';
import type { AuthTokens, AuthTokensDto } from '@/features/auth/types/auth.types';

const REDIRECT_URI = `${window.location.origin}/auth/callback`;
/** `azure` → the auth server co-issues the X-ZUMO-AUTH token for MEDIS /v2. */
const SCOPE = 'azure';

const VERIFIER_KEY = 'oauth.pkce.verifier';
const STATE_KEY = 'oauth.pkce.state';
const RETURN_KEY = 'oauth.return';
const TOKEN_URL = `${env.authOrigin}/oauth/token`;

/**
 * In-memory guard: true once a logout redirect has been kicked off. Clearing the
 * auth store re-renders the app → the route guard navigates to /login → LoginPage
 * auto-fires beginLogin(). That second navigation would CANCEL the in-flight
 * /logout navigation and silently re-login from the still-alive web session. The
 * flag makes beginLogin() a no-op until the full-page /logout navigation commits.
 * It's module-level (NOT sessionStorage) so it resets on the fresh load after
 * logout — the legitimate login on /login still runs.
 */
let loggingOut = false;

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLen = 48): string {
  const a = new Uint8Array(byteLen);
  crypto.getRandomValues(a);
  return base64url(a);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

async function postToken(body: URLSearchParams): Promise<AuthTokens | null> {
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body,
  });
  if (!res.ok) return null;
  return mapTokens((await res.json()) as AuthTokensDto);
}

/** Redirect to the hosted login page (authorize endpoint) to begin login. */
export async function beginLogin(returnTo?: string): Promise<void> {
  // A logout redirect is in flight — don't start a competing authorize redirect
  // (it would cancel the logout and silently re-login).
  if (loggingOut) return;

  const verifier = randomString(48);
  const state = randomString(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    client_id: env.authPublicClientId,
    response_type: 'code',
    redirect_uri: REDIRECT_URI,
    scope: SCOPE,
    state,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
  });
  window.location.href = `${env.authOrigin}/oauth/authorize?${params.toString()}`;
}

/** Handle the /auth/callback: validate state, exchange code (+verifier) for tokens. */
export async function completeLogin(): Promise<AuthTokens> {
  const url = new URL(window.location.href);
  const error = url.searchParams.get('error');
  if (error) throw new Error(url.searchParams.get('error_description') || error);

  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  if (!code) throw new Error('Cod de autorizare lipsă.');
  if (!state || state !== expectedState) throw new Error('Stare invalidă (posibil CSRF).');
  if (!verifier) throw new Error('Verifier PKCE lipsă.');

  const tokens = await postToken(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.authPublicClientId,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: verifier,
    }),
  );

  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  if (!tokens) throw new Error('Schimbul codului pe token a eșuat.');
  return tokens;
}

/** Where to go after a successful login (defaults to home). */
export function consumeReturnTo(): string {
  const r = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return r || '/';
}

/**
 * Auth-server logout URL — ends the web (session) login so the next login
 * requires credentials again, then returns to the SPA's /login.
 */
export function logoutUrl(): string {
  const redirect = `${window.location.origin}/login`;
  return `${env.authOrigin}/logout?redirect=${encodeURIComponent(redirect)}`;
}

/**
 * Begin logout: set the guard FIRST (so any re-render → /login → beginLogin is a
 * no-op), then do the full-page navigation to the auth server's /logout. Call
 * this instead of assigning window.location to logoutUrl() directly.
 */
export function beginLogout(): void {
  loggingOut = true;
  window.location.href = logoutUrl();
}

/** Refresh tokens directly against the auth server (public client, no secret). */
export function refreshViaAuth(refreshToken: string): Promise<AuthTokens | null> {
  return postToken(
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.authPublicClientId,
      refresh_token: refreshToken,
      scope: SCOPE,
    }),
  );
}
